// Uploaded background-music store. Audio files are far too large for localStorage,
// so the blobs live in IndexedDB (db "revault-music", store "tracks"); a lightweight
// index of { id, name, type } is mirrored in localStorage under MUSIC_KEY via the
// generic store, so the player + settings can list tracks synchronously and stay in
// sync across tabs. The audio never leaves this browser.
import { MUSIC_KEY } from "./settings-schema.js";
import { readJSON, writeJSON, subscribe } from "./store.js";

const DB_NAME = "revault-music";
const STORE = "tracks";
const MAX_TRACKS = 40;
const MAX_BYTES = 30 * 1024 * 1024; // 30 MB per file - a generous song, keeps the DB sane

let dbPromise = null;
function openDB() {
	if (dbPromise) return dbPromise;
	dbPromise = new Promise((resolve, reject) => {
		let req;
		try { req = indexedDB.open(DB_NAME, 1); } catch (e) { reject(e); return; }
		req.onupgradeneeded = () => { const db = req.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" }); };
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error || new Error("indexeddb"));
	});
	return dbPromise;
}
function tx(mode) { return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE)); }
function reqDone(r) { return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); }); }

// The synchronous index (names only) - what the UI renders from.
export function listTracks() {
	const v = readJSON(MUSIC_KEY, []);
	return Array.isArray(v) ? v : [];
}
export function subscribeTracks(fn) { return subscribe(MUSIC_KEY, fn); }

function newId() {
	try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
	return "t" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Add audio Files. Returns { added, skipped } and rejects only on a hard DB failure.
export async function addTracks(files) {
	const list = Array.from(files || []);
	let index = listTracks();
	let added = 0, skipped = 0;
	for (const f of list) {
		if (index.length + added >= MAX_TRACKS) { skipped++; continue; }
		if (!/^audio\//.test(f.type || "") && !/\.(mp3|m4a|aac|ogg|oga|wav|flac|opus|weba)$/i.test(f.name || "")) { skipped++; continue; }
		if (f.size > MAX_BYTES) { skipped++; continue; }
		const id = newId();
		const rec = { id, name: (f.name || "Track").replace(/\.[^.]+$/, "").slice(0, 80), type: f.type || "audio/mpeg", blob: f, addedAt: Date.now() };
		try {
			const store = await tx("readwrite");
			await reqDone(store.put(rec));
			index = index.concat([{ id, name: rec.name, type: rec.type }]);
			added++;
		} catch (e) { skipped++; }
	}
	if (added) writeJSON(MUSIC_KEY, index); // broadcasts to subscribers + other tabs
	return { added, skipped };
}

export async function getTrackBlob(id) {
	try { const store = await tx("readonly"); const rec = await reqDone(store.get(id)); return rec ? rec.blob : null; }
	catch (e) { return null; }
}

export async function removeTrack(id) {
	try { const store = await tx("readwrite"); await reqDone(store.delete(id)); } catch (e) {}
	writeJSON(MUSIC_KEY, listTracks().filter((t) => t.id !== id));
}

export async function clearTracks() {
	try { const store = await tx("readwrite"); await reqDone(store.clear()); } catch (e) {}
	writeJSON(MUSIC_KEY, []);
}
